<template>
    <div>
        <p class="text-muted small mb-1">Maximum {{ limit }} images autorisées</p>

        <Draggable class="list-group" v-model="list" :options="draggableOptions">
            <template v-for="(file,i) in list">
                <div class="list-group-item mb-0" style="cursor: move">
                    <div class="row align-items-center mx-n2 mx-lg-n3">
                        <template v-if="thumbnail(file)">
                            <div class="col-4 col-lg-3 order-1 order-lg-0 px-2 px-lg-3">
                                <div class="responsive-ratio responsive-ratio-1/1">
                                    <div class="responsive-ratio-item d-flex align-items-center justify-content-center">
                                        <img class="img-fluid mh-100" :src="thumbnail(file)" alt="">
                                    </div>
                                </div>
                            </div>
                        </template>
                        <div class="col order-1 order-lg-0 px-2 px-lg-3">
                            <div class="form-group">
                                <label :for="`file-${i}-legend`">Légende</label>
                                <input :id="`file-${i}-legend`" class="form-control" :value="file.legend" type="text" @input="handleLegendChanged(file, $event)">
                            </div>
                            <div class="form-group">
                                <label :for="`file-${i}-copyright`">Crédits&nbsp;<span class="text-danger">*</span></label>
                                <input :id="`file-${i}-copyright`"
                                    class="form-control"
                                    :class="{ 'is-invalid':error(i, 'copyright') }"
                                    type="text"
                                    :value="file.copyright"
                                    @input="handleCopyrightChanged(file, $event)"
                                >
                                <template v-if="error(i, 'copyright')">
                                    <div class="invalid-feedback">
                                        {{ error(i, 'copyright') }}
                                    </div>
                                </template>
                            </div>
                        </div>
                        <div class="col-12 col-lg-auto order-0 order-lg-1 px-2 px-lg-3">
                            <div class="text-right">
                                <button class="btn btn-sm btn-outline-danger" @click="handleDeleteClicked(file)">Supprimer</button>
                            </div>
                        </div>
                    </div>
                    <template v-if="file.state && file.state.uploading">
                        <div class="progress">
                            <div class="progress-bar"
                                role="progressbar"
                                :style="{ width:`${file.state.progress}%` }"
                                :aria-valuenow="file.state.progress"
                                aria-valuemin="0"
                                aria-valuemax="100">
                            </div>
                        </div>
                    </template>
                </div>
            </template>
        </Draggable>

        <div class="mt-3">
            <Dropzone id="images-uploader"
                :options="options"
                @vdropzone-file-added="handleAdded"
                @vdropzone-thumbnail="handleThumbnail"
                @vdropzone-upload-progress="handleUploadProgress"
                @vdropzone-error="handleError"
                @vdropzone-complete="handleCompleted"
                @vdropzone-removed-file="handleFileRemoved"
                @vdropzone-queue-complete="handleQueueCompleted"
                use-custom-slot
                ref="dropzone"
            >
                <div class="card">
                    <div class="card-body d-flex align-items-center justify-content-center text-muted" style="cursor: default">
                        <svg width="20" height="20" class="mr-2"><use xlink:href="#sprite-plus" style="fill: currentColor"></use></svg>
                        <div>
                            <a href="" @click.prevent>Parcourir</a> ou glisser-déposer un fichier...
                        </div>
                    </div>
                </div>
            </Dropzone>
        </div>

        <div class="text-right mt-3 pt-3 border-top">
            <button class="btn btn-outline-dark mr-2" @click="handleCancelClicked">Annuler</button>
            <button class="btn btn-primary" :disabled="isLoading" @click="handleSubmitClicked">
                <template v-if="isLoading">Chargement...</template>
                <template v-else>Enregistrer</template>
            </button>
        </div>
    </div>
</template>

<script>
    import Vue from 'vue';
    import Draggable from 'vuedraggable';
    import Dropzone from 'vue2-dropzone';
    import {errorAlert} from "../../util/alert";

    export default {
        components: {
            Draggable,
            Dropzone,
        },
        props: {
            files: Array,
            url: String,
            accept: String,
            maxSize: Number,
            csrfToken: String,
            limit: Number,
            errors: Object,
            loading: Boolean,

            /* data name in xhr (e.g. "visuals"), required for errors */
            name: {
                type: String,
                required: true,
            },
        },
        data() {
            return {
                list: this.files.map(file => ({ ...file })),
                uploading: false,
            }
        },
        computed: {
            options() {
                return {
                    url: this.url,
                    headers: {
                        'X-CSRF-TOKEN': this.csrfToken,
                    },
                    acceptedFiles: this.accept,
                    maxFilesize: this.maxSize,
                    dictFileTooBig: `Fichier trop volumineux (max. ${this.maxSize}Mo)`,
                    dictMaxFilesExceeded: `${this.limit} fichiers maximum`,
                    maxFiles: this.limit - (this.files || []).length,
                    previewTemplate: '<div></div>',
                }
            },
            draggableOptions() {
                return {
                    filter: 'input',
                    preventOnFilter: false,
                }
            },
            isLoading() {
                return this.loading || this.uploading;
            },
        },
        methods: {
            thumbnail(file) {
                return file.dataURL || file.thumbnail;
            },
            error(i, field) {
                const name = `${this.name}.${i}.${field}`;
                return this.errors && Array.isArray(this.errors[name])
                    ? this.errors[name][0]
                    : null;
            },
            handleAdded(file) {
                file.state = Vue.observable({
                    progress: 0,
                    uploading: true,
                });
                this.list = [...this.list, file];
                this.uploading = true;
            },
            handleError(file, error, xhr) {
                let message = null;
                if(xhr) {
                    message = xhr.status === 422
                        ? error.errors.file[0]
                        : 'Une erreur est survenue lors du téléchargement du fichier, veuillez réessayer';
                } else {
                    message = error;
                }
                errorAlert(this, message);
                this.$refs.dropzone.removeFile(file);
            },
            handleUploadProgress(file, progress) {
                file.state.progress = progress;
            },
            handleThumbnail() {
                this.list = [...this.list];
            },
            handleFileRemoved(file) {
                this.list = this.list.filter(f => f !== file);
            },
            handleCompleted(file) {
                file.state.uploading = false;
            },
            handleQueueCompleted() {
                this.uploading = false;
            },
            handleLegendChanged(file, e) {
                file.legend = e.target.value;
            },
            handleCopyrightChanged(file, e) {
                file.copyright = e.target.value;
            },
            handleDeleteClicked(file) {
                this.$refs.dropzone.removeFile(file);
            },
            handleCancelClicked() {
                this.$emit('cancel');
            },
            handleSubmitClicked() {
                if(this.uploading) {
                    return;
                }
                this.$emit('submit', this.list
                    .filter(file => file.id || file.status === 'success')
                    .map(file => file instanceof File ? {
                        file_name: JSON.parse(file.xhr.responseText).name,
                        legend: file.legend || '',
                        copyright: file.copyright || '',
                    } : file)
                );
            },
        },
    }
</script>